/**
 * Search Dropdown Module
 * Handles search functionality with dropdown results
 */

import { escapeHtml, debounce } from '../core/utils.js';

export class SearchDropdown {
    constructor(system) {
        this.system = system;
        this.instances = new Map();
    }

    init() {
        // Automatisk initialisering av vanliga sök-containers
        const searchContainers = [
            'search_container',
            'search-container', 
            'searchContainer',
            'header-search',
            'menu-search'
        ];

        let foundContainers = 0;

        searchContainers.forEach(containerId => {
            const container = document.getElementById(containerId);
            if (container && !this.instances.has(containerId)) {
                console.log(`SearchDropdown: Auto-initialiserar sökfält för container '${containerId}'`);
                this.create(containerId);
                foundContainers++;
            }
        });

        // Sök efter containers med data-search attribut
        const dataSearchContainers = document.querySelectorAll('[data-search]');
        dataSearchContainers.forEach(container => {
            if (container.id && !this.instances.has(container.id)) {
                console.log(`SearchDropdown: Auto-initialiserar sökfält för container '${container.id}' (data-search)`);
                this.create(container.id);
                foundContainers++;
            }
        });

        // Early return if no search elements found - this page doesn't have search
        if (foundContainers === 0) {
            console.log('SearchDropdown: No search containers found, skipping initialization');
            return;
        }

        console.log(`SearchDropdown: Initialized ${foundContainers} search container(s)`);
    }

    create(containerId, options = {}) {
        const config = {
            placeholder: 'Sök produkter...',
            minChars: this.system.config.searchMinChars,
            debounceMs: this.system.config.searchDebounceMs,
            maxResults: this.system.config.searchMaxResults,
            showCategories: this.system.config.showCategories,
            apiUrl: this.system.config.searchApi,
            ...options
        };

        const container = document.getElementById(containerId);
        if (!container) {
            console.error(`SearchDropdown: Element med ID '${containerId}' hittades inte`);
            return null;
        }

        // Skapa instance
        const instance = new SearchDropdownInstance(container, config, this.system);
        this.instances.set(containerId, instance);
        
        return instance;
    }

    destroy(containerId) {
        const instance = this.instances.get(containerId);
        if (instance) {
            instance.destroy();
            this.instances.delete(containerId);
        }
    }

    destroyAll() {
        this.instances.forEach(instance => instance.destroy());
        this.instances.clear();
    }
}

/**
 * Search Dropdown Instance
 */
export class SearchDropdownInstance {
    constructor(container, config, system) {
        this.container = container;
        this.config = config;
        this.system = system;
        this.isVisible = false;
        this.currentQuery = '';
        this.debouncedSearch = debounce(this.performSearch.bind(this), config.debounceMs);
        
        this.init();
    }

    init() {
        this.createHTML();
        this.cacheElements();
        this.setupEventListeners();
    }

    createHTML() {
        this.container.innerHTML = `
            <div class="search-dropdown-wrapper">
                <div class="search-input-wrapper">
                    <input type="text" 
                           class="search-input" 
                           placeholder="${escapeHtml(this.config.placeholder)}"
                           autocomplete="off">
                    <button class="search-clear" style="display: none;" type="button">×</button>
                </div>
                <div class="search-dropdown" style="display: none;">
                    <div class="search-dropdown-content"></div>
                </div>
            </div>
        `;
    }

    cacheElements() {
        this.input = this.container.querySelector('.search-input');
        this.dropdown = this.container.querySelector('.search-dropdown');
        this.dropdownContent = this.container.querySelector('.search-dropdown-content');
        this.clearBtn = this.container.querySelector('.search-clear');
    }

    setupEventListeners() {
        // Input events
        this.input.addEventListener('input', (e) => this.handleInput(e));
        this.input.addEventListener('focus', () => this.handleFocus());
        this.input.addEventListener('keydown', (e) => this.handleKeydown(e));
        
        // Clear button
        this.clearBtn.addEventListener('click', () => this.clearSearch());
        
        // Outside click
        this.outsideClickHandler = (e) => {
            if (!this.container.contains(e.target)) {
                this.hideDropdown();
            }
        };
        document.addEventListener('click', this.outsideClickHandler);
    }

    handleInput(e) {
        const query = e.target.value.trim();
        this.currentQuery = query;
        
        this.clearBtn.style.display = query ? 'block' : 'none';
        
        if (query.length >= this.config.minChars) {
            this.debouncedSearch(query);
        } else {
            this.hideDropdown();
        }
    }

    handleFocus() {
        if (this.currentQuery.length >= this.config.minChars && this.dropdownContent.innerHTML.trim()) {
            this.showDropdown();
        }
    }

    handleKeydown(e) {
        if (e.key === 'Escape') {
            this.hideDropdown();
            this.input.blur();
        }
    }

    clearSearch() {
        this.input.value = '';
        this.currentQuery = '';
        this.clearBtn.style.display = 'none';
        this.hideDropdown();
        this.input.focus();
    }

    async performSearch(query) {
        try {
            this.showLoading();
            
            const url = `${this.config.apiUrl}?query=${encodeURIComponent(query)}&pageSize=${this.config.maxResults}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: { 'accept': '*/*' }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            
            if (this.currentQuery === query) {
                this.displayResults(data);
            }
            
        } catch (error) {
            console.error('SearchDropdown: API error:', error);
            if (this.currentQuery === query) {
                this.showError('Kunde inte utföra sökning');
            }
        }
    }

    displayResults(data) {
        const { products, categories, brands, searchPageUrl, searchTerm } = data;
        let html = '';

        // Produkter
        if (products?.items?.length > 0) {
            html += this.renderProductsSection(products.items);
        }

        // Kategorier
        if (this.config.showCategories && categories?.items?.length > 0) {
            html += this.renderCategoriesSection(categories.items);
        }

        // Varumärken
        if (this.config.showCategories && brands?.items?.length > 0) {
            html += this.renderBrandsSection(brands.items);
        }

        // "Visa alla"-länk
        if (searchPageUrl && searchTerm) {
            html += this.renderViewAllSection(searchPageUrl, searchTerm, products, categories, brands);
        }

        // Inga resultat
        if (!html) {
            html = '<div class="search-no-results">Inga resultat hittades</div>';
        }

        this.dropdownContent.innerHTML = html;
        this.showDropdown();
    }

    renderProductsSection(products) {
        let html = '<div class="search-section">';
        html += '<div class="search-section-header">Produkter</div>';
        
        products.forEach(product => {
            const price = product.discountPrice || product.price;
            html += `
                <a href="${escapeHtml(product.productUrl || '#')}" class="search-item search-product">
                    <div class="search-item-image">
                        <img src="${escapeHtml(product.firstImage || '')}" 
                             alt="${escapeHtml(product.name)}"
                             loading="lazy"
                             onerror="this.style.display='none'" />
                    </div>
                    <div class="search-item-content">
                        <div class="search-item-name">${escapeHtml(product.name)}</div>
                        <div class="search-item-category">${escapeHtml(product.categoryName || '')}</div>
                        <div class="search-item-price">${price} kr</div>
                    </div>
                </a>
            `;
        });
        
        return html + '</div>';
    }

    renderCategoriesSection(categories) {
        let html = '<div class="search-section">';
        html += '<div class="search-section-header">Kategorier</div>';
        
        categories.forEach(category => {
            html += `
                <a href="${escapeHtml(category.categoryUrl || '#')}" class="search-item search-category">
                    <div class="search-item-image">
                        <img src="${escapeHtml(category.imageUrl || '')}" 
                             alt="${escapeHtml(category.name)}"
                             loading="lazy"
                             onerror="this.style.display='none'" />
                    </div>
                    <div class="search-item-content">
                        <div class="search-item-name">${escapeHtml(category.name)}</div>
                        <div class="search-item-description">${escapeHtml(category.description || '')}</div>
                        <div class="search-item-count">${category.activeProductCount || 0} produkter</div>
                    </div>
                </a>
            `;
        });
        
        return html + '</div>';
    }

    renderBrandsSection(brands) {
        let html = '<div class="search-section">';
        html += '<div class="search-section-header">Varumärken</div>';
        
        brands.forEach(brand => {
            html += `
                <a href="${escapeHtml(brand.brandUrl || '#')}" class="search-item search-brand">
                    <div class="search-item-image">
                        <img src="${escapeHtml(brand.imageUrl || '')}" 
                             alt="${escapeHtml(brand.name)}"
                             loading="lazy"
                             onerror="this.style.display='none'" />
                    </div>
                    <div class="search-item-content">
                        <div class="search-item-name">${escapeHtml(brand.name)}</div>
                        <div class="search-item-count">${brand.activeProductCount || 0} produkter</div>
                    </div>
                </a>
            `;
        });
        
        return html + '</div>';
    }

    renderViewAllSection(searchPageUrl, searchTerm, products, categories, brands) {
        const totalResults = (products?.totalItems || 0) + (categories?.totalItems || 0) + (brands?.totalItems || 0);
        const searchUrl = `${searchPageUrl}?q=${encodeURIComponent(searchTerm)}`;
        
        return `
            <div class="search-footer">
                <a href="${escapeHtml(searchUrl)}" class="search-view-all">
                    Visa alla sökresultat (${totalResults})
                </a>
            </div>
        `;
    }

    showLoading() {
        this.dropdownContent.innerHTML = '<div class="search-loading">Söker...</div>';
        this.showDropdown();
    }

    showError(message) {
        this.dropdownContent.innerHTML = `<div class="search-error">${escapeHtml(message)}</div>`;
        this.showDropdown();
    }

    showDropdown() {
        this.dropdown.style.display = 'block';
        this.isVisible = true;
    }

    hideDropdown() {
        this.dropdown.style.display = 'none';
        this.isVisible = false;
    }

    focus() {
        this.input.focus();
    }

    clear() {
        this.clearSearch();
    }

    getVisibility() {
        return this.isVisible;
    }

    destroy() {
        document.removeEventListener('click', this.outsideClickHandler);
        this.container.innerHTML = '';
    }
}
